import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { PROJECT_COST_LINE_TYPE_OPTIONS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { CostType, ProjectCostLine } from '@/types';
import { router, useForm } from '@inertiajs/react';
import { format } from 'date-fns';

import { useTranslations } from '@/hooks/useTranslations';
import { CalendarIcon } from 'lucide-react';
import { FormEventHandler, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface EditProjectCostModalProps {
    cost: ProjectCostLine;
    isOpen: boolean;
    onClose: () => void;
}

type ProjectCostFormData = {
    description: string;
    amount: string;
    type: string;
    incurred_date: Date;
};

export default function EditProjectCostModal({ cost, isOpen, onClose }: EditProjectCostModalProps) {
    const { t } = useTranslations();
    const [dateOpen, setDateOpen] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { data, setData, errors, reset } = useForm<ProjectCostFormData>({
        description: cost.description,
        amount: cost.amount.toString(),
        type: cost.type,
        incurred_date: cost.date_incurred ? new Date(cost.date_incurred) : new Date(),
    });

    // Reset form when cost changes
    useEffect(() => {
        setData({
            description: cost.description,
            amount: cost.amount.toString(),
            type: cost.type,
            incurred_date: cost.date_incurred ? new Date(cost.date_incurred) : new Date(),
        });
    }, [cost]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setProcessing(true);

        router.put(
            `/projects/${cost.project_id}/cost-lines/${cost.id}`,
            {
                description: data.description,
                amount: data.amount,
                type: data.type,
                incurred_date: data.incurred_date,
            },
            {
                onSuccess: () => {
                    onClose();
                },
                onError: (errors: Record<string, string>) => {
                    console.error('Update failed:', errors);
                    toast.error(t('projects.forms.edit_cost.messages.failed_to_update', 'Failed to update cost'));
                },
                onFinish: () => {
                    setProcessing(false);
                },
            },
        );
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>Edit Cost</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('projects.forms.edit_cost.description', 'Edit this cost line for the project.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="description">{t('common.description', 'Description')}</Label>
                            <Input
                                id="description"
                                value={data.description}
                                onChange={(e) => setData('description', e.target.value)}
                                placeholder={t('projects.forms.edit_cost.placeholders.description', 'Describe what this cost is for...')}
                                className={errors.description ? 'border-destructive' : ''}
                            />
                            {errors.description && <p className="text-destructive mt-1 text-sm">{errors.description}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="type">{t('projects.forms.edit_cost.labels.type', 'Type')}</Label>
                            <Combobox
                                options={PROJECT_COST_LINE_TYPE_OPTIONS}
                                value={data.type}
                                onValueChange={(value) => setData('type', value as CostType)}
                                placeholder={t('projects.forms.edit_cost.placeholders.select_type', 'Select cost type')}
                                className={cn(errors.type && 'border-destructive')}
                            />
                            {errors.type && <p className="text-destructive mt-1 text-sm">{errors.type}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="amount">{t('projects.forms.edit_cost.labels.amount', 'Amount (EUR)')}</Label>
                            <Input
                                id="amount"
                                type="number"
                                step="0.01"
                                value={data.amount}
                                onChange={(e) => setData('amount', e.target.value)}
                                placeholder={t('projects.forms.edit_cost.placeholders.amount', '0.00')}
                                className={errors.amount ? 'border-destructive' : ''}
                            />
                            {errors.amount && <p className="text-destructive mt-1 text-sm">{errors.amount}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label>{t('projects.forms.edit_cost.labels.date_incurred', 'Date Incurred')}</Label>
                            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={'outline'}
                                        className={cn(
                                            'w-full justify-start text-left font-normal',
                                            !data.incurred_date && 'text-muted-foreground',
                                            errors.incurred_date && 'border-destructive',
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {data.incurred_date
                                            ? format(data.incurred_date, 'yyyy-MM-dd')
                                            : t('projects.forms.edit_cost.placeholders.pick_date', 'Pick a date')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={data.incurred_date}
                                        onSelect={(date) => {
                                            setData('incurred_date', date ?? new Date());
                                            setDateOpen(false);
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                            {errors.incurred_date && <p className="text-destructive mt-1 text-sm">{errors.incurred_date}</p>}
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing ? t('common.saving', 'Saving...') : t('projects.forms.edit_cost.buttons.update', 'Update Cost')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
